import { Link, useRouterState } from "@tanstack/react-router";
import {
  Building2,
  Calculator,
  Coins,
  FileSpreadsheet,
  FileText,
  History,
  Images,
  LayoutDashboard,
  Link2,
  ListChecks,
  Palette,
  Repeat2,
  Upload,
  Users,
  Wallet,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { api } from "@/lib/api";

// ===========================================================================
// La navegación, con el mismo patrón que el ERP: grupos con etiqueta, el
// componente `Sidebar` de shadcn —que se pliega a iconos y recuerda su estado—
// y el elemento activo marcado por la ruta.
//
// Se copia la estructura y no solo el aspecto: cuando este módulo se mude al
// ERP, esta navegación se convierte en un grupo más de la suya.
// ===========================================================================

const GRUPOS = [
  {
    label: "Conciliación",
    items: [
      { to: "/", icon: LayoutDashboard, label: "Inicio" },
      // La cola del trabajo real: el expediente del cliente con sus vouchers
      // por atar. Lleva el número de los que siguen pendientes, con la misma
      // condición que usa su propia cola.
      { to: "/conciliar", icon: Link2, label: "Conciliar expediente" },
      { to: "/carga", icon: Upload, label: "Cargar el mes" },
      // Dos entradas y no una: cinco extractos al mes contra ciento cincuenta
      // expedientes son dos trabajos con dos ritmos, y juntos los cinco se
      // perdían entre los ciento cincuenta — que son justo los que impiden
      // cerrar el mes.
      {
        to: "/documentos/clientes",
        icon: FileText,
        label: "Docs. de clientes",
      },
      {
        to: "/documentos/estados-de-cuenta",
        icon: FileSpreadsheet,
        label: "Estados de cuenta",
      },
      // Detrás de los estados de cuenta: los movimientos SALEN de ellos, y
      // leerlos en ese orden es el orden en que llega el trabajo.
      { to: "/movimientos", icon: Repeat2, label: "Movimientos" },
      { to: "/compartidas", icon: Images, label: "Fotos repetidas" },
      // El producto final tiene que estar en el menú. Estaba servido y
      // probado, y la única forma de verlo era un botón al final del cierre.
      { to: "/asientos", icon: Calculator, label: "Excel para CONCAR" },
    ],
  },
  {
    label: "Maestros",
    items: [
      { to: "/empresas", icon: Building2, label: "Empresas y cuentas" },
      { to: "/tipo-cambio", icon: Coins, label: "Tipo de cambio" },
    ],
  },
  {
    label: "Control",
    items: [
      // Control es de jefatura para arriba: la auditoría dice quién hizo qué
      // y no es lectura para cualquiera. Se esconde ADEMÁS de estar cerrada en
      // el servidor — enseñar una puerta que devuelve 403 parece un fallo del
      // sistema y no un permiso que no se tiene.
      {
        to: "/conciliaciones",
        icon: ListChecks,
        label: "Conciliaciones",
        desdeJefe: true,
      },
      { to: "/auditoria", icon: History, label: "Auditoría", desdeJefe: true },
      // Solo para quien administra. Se esconde además de estar cerrada en el
      // servidor: enseñar una puerta que devuelve 403 al abrirla parece un
      // fallo del sistema y no un permiso que no se tiene.
      { to: "/usuarios", icon: Users, label: "Usuarios", soloAdmin: true },
    ],
  },
  {
    label: "Referencia",
    items: [{ to: "/piezas", icon: Palette, label: "Piezas del diseño" }],
  },
] as const;

export function AppSidebar() {
  const ruta = useRouterState({ select: (s) => s.location.pathname });

  // Los contadores de la lámina: lo que espera en cada sección, sin tener que
  // entrar a mirarlo. El ERP hace lo mismo con su `Badge` de pendientes.
  const { data } = api.inicio.useQuery(["inicio"], undefined, {
    staleTime: 60_000,
  });
  const d = data?.status === 200 ? data.body : null;

  const { data: quienSoy } = api.yo.useQuery(["yo"], undefined, {
    staleTime: 300_000,
  });
  const administro =
    quienSoy?.status === 200 && quienSoy.body.role === "administrador";
  // De jefatura para arriba. Es la misma escalera que el servidor
  // (`MINIMO_PARA.supervisar`): la pantalla esconde lo que la API ya cierra,
  // para que nadie se choque con un 403 que parece un fallo del sistema.
  const supervisa =
    quienSoy?.status === 200 &&
    ["jefe", "gerente", "administrador"].includes(quienSoy.body.role);
  const cuentas: Record<string, number | undefined> = {
    "/conciliar": d?.expedientesPorConciliar,
    "/movimientos": d?.necesitaPersona,
    // Lo que FALTA, como los otros dos de arriba: `adjuntos` cuenta imágenes
    // extraídas de los PDF, así que con cuatro expedientes cargados la lámina
    // decía 35 y la pantalla, 4.
    "/documentos/clientes": d?.expedientesIncompletos,
    "/empresas": d?.empresasTotales,
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <Link
          to="/"
          className="flex items-center gap-2 px-2 py-1.5 font-semibold text-sidebar-foreground"
        >
          <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Wallet className="size-4" />
          </div>
          <span className="group-data-[collapsible=icon]:hidden">
            Conciliación
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {GRUPOS.map((grupo) => (
          <SidebarGroup key={grupo.label}>
            <SidebarGroupLabel>{grupo.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {grupo.items
                  .filter(
                    (item) =>
                      (!("soloAdmin" in item && item.soloAdmin) ||
                        administro) &&
                      (!("desdeJefe" in item && item.desdeJefe) || supervisa),
                  )
                  .map((item) => {
                    const activa =
                      item.to === "/" ? ruta === "/" : ruta.startsWith(item.to);
                    return (
                      <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton
                          asChild
                          isActive={activa}
                          tooltip={item.label}
                        >
                          <Link to={item.to}>
                            <item.icon className="size-4" />
                            <span className="flex-1">{item.label}</span>
                            {cuentas[item.to] ? (
                              <Badge
                                variant="secondary"
                                className="tabular-nums group-data-[collapsible=icon]:hidden"
                              >
                                {(cuentas[item.to] ?? 0).toLocaleString(
                                  "es-PE",
                                )}
                              </Badge>
                            ) : null}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      {/* El pie de la lámina: qué es este sistema, en dos líneas. */}
      <SidebarFooter className="group-data-[collapsible=icon]:hidden">
        <div className="rounded-md bg-sidebar-accent/50 px-3 py-2 text-[11px] text-sidebar-foreground/70">
          <div className="flex items-center gap-2 font-medium text-sidebar-foreground text-xs">
            <span className="size-1.5 rounded-full bg-emerald-500" />
            Sistema listo
          </div>
          <div className="mt-0.5">Multiempresa · soles y dólares</div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
